import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { get, post } from '@/lib/api';

/**
 * Style Service - 风格和分类管理服务
 * Base URL: /api/projects/styles
 * 管理视频生成风格和分类的CRUD操作
 */

export interface Style {
  id: string;
  name: string;
  name_cn?: string;
  name_ja?: string;
  description?: string;
  thumbnail?: string;
  category_id?: string;
  attributes?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

export interface StyleCategory {
  id: string;
  name: string;
  description?: string;
  parent_id?: string;
  sort_order?: number;
  created_at?: string;
}

interface RawStyleItem {
  id: string | number;
  name: string;
  name_cn?: string;
  name_ja?: string;
  description?: string;
  url?: string;
  style_type?: string;
  scene_urls?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

interface StyleState {
  items: Style[];
  categories: StyleCategory[];
  currentStyle: Style | null;
  loadingStyles: boolean;
  loadingCategories: boolean;
  error: string | null;
}

const initialState: StyleState = {
  items: [],
  categories: [],
  currentStyle: null,
  loadingStyles: false,
  loadingCategories: false,
  error: null,
};

/**
 * Fetch all styles
 * GET /api/projects/styles
 * @returns Array of styles
 */
export const fetchStyles = createAsyncThunk(
  'style/fetchStyles',
  async (params?: { category_id?: string; page?: number; limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.category_id) query.set('category_id', params.category_id);
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    const response = await get<{ data?: { list?: RawStyleItem[] | null; total?: number; page?: number } }>(
      `/api/projects/styles${query.toString() ? `?${query.toString()}` : ''}`
    );
    return (response.data?.list ?? []).map((item): Style => ({
      id: String(item.id),
      name: item.name,
      name_cn: item.name_cn,
      name_ja: item.name_ja,
      description: item.description,
      thumbnail: item.url,
      category_id: item.style_type,
      attributes: item.scene_urls,
      created_at: item.created_at,
      updated_at: item.updated_at,
    }));
  }
);

/**
 * Fetch single style details
 * GET /api/projects/styles/:id
 * @param id - Style ID
 * @returns Style details
 */
export const fetchStyleDetail = createAsyncThunk(
  'style/fetchDetail',
  async (id: string) => {
    const data = await get<Style>(`/api/projects/styles/${id}`);
    return data;
  }
);

/**
 * Create a new style
 * POST /api/projects/styles
 * @param styleData - Style creation data (name, description, projectId)
 * @returns Created style
 */
export const createStyle = createAsyncThunk(
  'style/createStyle',
  async (styleData: { name: string; description?: string; thumbnail?: string; category_id?: string }) => {
    const data = await post<Style>('/api/projects/styles', styleData);
    return data;
  }
);

/**
 * Update an existing style
 * POST /api/projects/styles/:id
 * @param id - Style ID and update data
 * @returns Updated style
 */
export const updateStyle = createAsyncThunk(
  'style/updateStyle',
  async ({ id, ...styleData }: Partial<Style> & { id: string }) => {
    const data = await post<Style>(`/api/projects/styles/${id}`, styleData);
    return data;
  }
);

/**
 * Delete a style
 * POST /api/projects/styles/:id/delete
 * @param id - Style ID to delete
 * @returns Deleted style ID
 */
export const deleteStyle = createAsyncThunk(
  'style/deleteStyle',
  async (id: string) => {
    await post(`/api/projects/styles/${id}/delete`);
    return id;
  }
);

/**
 * Fetch all style categories
 * GET /api/projects/styles/categories
 * @returns Array of style categories
 */
export const fetchStyleCategories = createAsyncThunk(
  'style/fetchCategories',
  async () => {
    const response = await get<{ data?: { categories?: StyleCategory[] } }>('/api/projects/styles/categories');
    return response.data?.categories ?? [];
  }
);

/**
 * Create a new style category
 * POST /api/projects/styles/categories
 * @param categoryData - Category creation data (name, description, order)
 * @returns Created category
 */
export const createStyleCategory = createAsyncThunk(
  'style/createCategory',
  async (categoryData: { name: string; description?: string; sort_order?: number; parent_id?: string }) => {
    const data = await post<StyleCategory>('/api/projects/styles/categories', categoryData);
    return data;
  }
);

/**
 * Update an existing style category
 * PUT /api/projects/styles/categories/:id
 * @param id - Category ID and update data
 * @returns Updated category
 */
export const updateStyleCategory = createAsyncThunk(
  'style/updateCategory',
  async ({ id, ...data }: Partial<StyleCategory> & { id: string }) => {
    const result = await post<StyleCategory>(`/api/projects/styles/categories/${id}`, data);
    return result;
  }
);

/**
 * Delete a style category
 * POST /api/projects/styles/categories/:id/delete
 * @param id - Category ID to delete
 * @returns Deleted category ID
 */
export const deleteStyleCategory = createAsyncThunk(
  'style/deleteCategory',
  async (id: string) => {
    await post(`/api/projects/styles/categories/${id}/delete`);
    return id;
  }
);

const styleSlice = createSlice({
  name: 'style',
  initialState,
  reducers: {
    setCurrentStyle: (state, action: PayloadAction<Style | null>) => {
      state.currentStyle = action.payload;
    },
    setStyleError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    clearStyleError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStyles.pending, (state) => {
        state.loadingStyles = true;
        state.error = null;
      })
      .addCase(fetchStyles.fulfilled, (state, action) => {
        state.loadingStyles = false;
        state.items = action.payload;
      })
      .addCase(fetchStyles.rejected, (state, action) => {
        state.loadingStyles = false;
        state.error = action.error.message || 'Failed to fetch styles';
      })
      .addCase(fetchStyleDetail.fulfilled, (state, action) => {
        state.currentStyle = action.payload;
      })
      .addCase(createStyle.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
      })
      .addCase(updateStyle.fulfilled, (state, action) => {
        const index = state.items.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        if (state.currentStyle?.id === action.payload.id) {
          state.currentStyle = action.payload;
        }
      })
      .addCase(deleteStyle.fulfilled, (state, action) => {
        state.items = state.items.filter(s => s.id !== action.payload);
        if (state.currentStyle?.id === action.payload) {
          state.currentStyle = null;
        }
      })
      .addCase(fetchStyleCategories.pending, (state) => {
        state.loadingCategories = true;
      })
      .addCase(fetchStyleCategories.fulfilled, (state, action) => {
        state.loadingCategories = false;
        state.categories = action.payload;
      })
      .addCase(fetchStyleCategories.rejected, (state) => {
        state.loadingCategories = false;
      })
      .addCase(createStyleCategory.fulfilled, (state, action) => {
        state.categories.push(action.payload);
      })
      .addCase(updateStyleCategory.fulfilled, (state, action) => {
        const index = state.categories.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.categories[index] = action.payload;
        }
      })
      .addCase(deleteStyleCategory.fulfilled, (state, action) => {
        state.categories = state.categories.filter(c => c.id !== action.payload);
      });
  },
});

export const { setCurrentStyle, setStyleError, clearStyleError } = styleSlice.actions;
export default styleSlice.reducer;
